import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { adminInvoicesApi } from "@/lib/commerce";
import { downloadInvoicePdf } from "@/lib/pdf";
import { formatMoney } from "@/lib/format";
import { toast } from "sonner";

export default function AdminInvoices() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setBusy(true); setError("");
    try { setData(await adminInvoicesApi.list({ page, page_size: 25 })); }
    catch (e) { setError(e.response?.data?.error?.message || "Failed to load invoices."); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [page]);

  return (
    <div className="min-h-screen bg-background" data-testid="admin-invoices-root">
      <header className="border-b bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin › Invoices</div>
            <div className="text-lg font-semibold">Tax invoices</div>
          </div>
          <Button asChild variant="outline"><Link to="/admin">&larr; Dashboard</Link></Button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-10 space-y-6">
        {error && (
          <div className="text-sm text-destructive border border-destructive/40 rounded p-3">{error}</div>
        )}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">All invoices</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-3 text-left">Invoice #</th>
                    <th className="p-3 text-left">Order #</th>
                    <th className="p-3 text-left">Customer</th>
                    <th className="p-3 text-right">Total</th>
                    <th className="p-3 text-left">Issued</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((inv) => (
                    <tr key={inv.id} className="border-t" data-testid={`admin-invoice-row-${inv.invoice_number}`}>
                      <td className="p-3 font-mono text-xs">{inv.invoice_number}</td>
                      <td className="p-3 font-mono text-xs">{inv.order_number}</td>
                      <td className="p-3">{inv.customer_snapshot?.name}</td>
                      <td className="p-3 text-right">
                        {formatMoney({ amount_minor: inv.totals?.grand_total_minor || 0, currency: inv.totals?.currency || "AED" })}
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {inv.issued_at && new Date(inv.issued_at).toLocaleString()}
                      </td>
                      <td className="p-3 text-right">
                        <Button
                          size="sm" variant="ghost"
                          onClick={async () => {
                            try { await downloadInvoicePdf(inv.id, inv.invoice_number); }
                            catch (e) { toast.error("Failed to download PDF."); }
                          }}
                          data-testid={`admin-invoice-pdf-${inv.invoice_number}`}
                        >
                          PDF
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {data.items.length === 0 && !busy && (
                    <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">
                      No invoices yet. Issue one from an order.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {data.total > 25 && (
              <div className="flex justify-between items-center mt-4">
                <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="admin-invoices-prev">Previous</Button>
                <div className="text-sm text-muted-foreground">Page {page}</div>
                <Button variant="outline" disabled={page * 25 >= data.total} onClick={() => setPage(page + 1)} data-testid="admin-invoices-next">Next</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
